import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Image as ImageIcon } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { PILLARS, PillarBadge } from "@/constants/pillars";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function MarketplacePage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [offers, setOffers] = useState([]);
  const [filter, setFilter] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", price: "", pillar: "asset", image_url: null });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get("/offers", { params: filter ? { pillar: filter } : {} });
    setOffers(data);
  };
  useEffect(() => { load(); }, [filter]);

  const uploadImg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/upload", fd); setForm((s) => ({ ...s, image_url: data.url })); toast.success("Immagine caricata"); }
    catch { toast.error("Upload fallito"); }
  };

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/offers", { ...form, price: parseFloat(form.price) || 0 });
      setOpen(false); setForm({ title: "", description: "", price: "", pillar: "asset", image_url: null });
      toast.success("Offerta creata"); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-2xl font-bold">{t("marketplace")}</h1>
        <Button data-testid="create-offer-btn" onClick={() => setOpen(true)} className="h-10 rounded-full bg-black text-white text-sm"><Plus size={16} className="mr-1" />{t("offer")}</Button>
      </div>
      <div className="flex gap-2 overflow-x-auto mb-4 pb-1">
        <button data-testid="mk-filter-all" onClick={() => setFilter(null)} className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold ${!filter ? "bg-black text-white" : "bg-neutral-100 text-neutral-600"}`}>{t("filter_all")}</button>
        {PILLARS.map((p) => <button key={p.id} data-testid={`mk-filter-${p.id}`} onClick={() => setFilter(p.id)} className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold ${filter === p.id ? "bg-black text-white" : "bg-neutral-100 text-neutral-600"}`}>{p.name}</button>)}
      </div>

      {offers.length === 0 ? (
        <p className="text-center text-neutral-400 py-10 text-sm">{t("no_offers")}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {offers.map((o) => (
            <motion.button key={o.id} data-testid={`offer-card-${o.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              onClick={() => navigate(`/marketplace/${o.id}`)}
              className="text-left border border-neutral-200 rounded-2xl overflow-hidden hover:border-black transition-colors">
              <div className="aspect-[4/3] bg-neutral-100">
                {o.image_url ? <img src={fileUrl(o.image_url)} alt={o.title} className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-neutral-300"><ImageIcon size={28} /></div>}
              </div>
              <div className="p-3">
                <PillarBadge id={o.pillar} />
                <h3 className="font-semibold text-sm mt-1.5 line-clamp-2">{o.title}</h3>
                <p className="font-display font-bold mt-1">€{Number(o.price).toFixed(2)}</p>
                <p className="text-[11px] text-neutral-400 mt-0.5">{o.author?.name}</p>
              </div>
            </motion.button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Nuova offerta</DialogTitle></DialogHeader>
          <Input data-testid="offer-title" placeholder="Titolo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11" />
          <Textarea data-testid="offer-desc" placeholder="Descrizione" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2">
            <Input data-testid="offer-price" type="number" placeholder="Prezzo €" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-11 w-32" />
            <Select value={form.pillar} onValueChange={(v) => setForm({ ...form, pillar: v })}>
              <SelectTrigger data-testid="offer-pillar" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{PILLARS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.image_url ? <img src={fileUrl(form.image_url)} alt="" className="rounded-xl h-32 w-full object-cover" /> :
            <button data-testid="offer-add-image" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 text-sm text-neutral-500"><ImageIcon size={18} />Aggiungi immagine</button>}
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadImg} />
          <Button data-testid="offer-submit" onClick={create} disabled={busy} className="w-full h-11 rounded-full bg-black text-white">{busy ? "Creo…" : "Crea offerta"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
